import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Bell, Shield, Key, FileWarning, Settings, ExternalLink, Check, Clock, Globe, HashIcon } from 'lucide-react';
import { useTeams } from '@/contexts/TeamsContext';
import { AuthContext } from '@/contexts/AuthContext';
import { Notification, NotificationsApi } from 'api-client';
import { getApiConfiguration } from '@/api/utils';
import { formatTimestamp } from '@/utils/dateTime';
import { severityOptions } from '@/data/findings-filters';
import { cn } from '@/lib/utils';

/**
 * Base notification interface with common properties
 */
interface BaseNotification {
  id: string;
  title: string;
  description: string;
  timestamp: string;
  read: boolean;
}

/**
 * Finding-specific notification properties
 */
interface FindingNotification extends BaseNotification {
  type: 'security' | 'credential' | 'data' | 'system';
  category: 'finding';
  summary?: {
    total: number;
    bySeverity: {
      critical: number;
      high: number;
      medium: number;
      low: number;
    };
  };
}

/**
 * Asset-specific notification properties
 */
interface AssetNotification extends BaseNotification {
  type: 'asset_discovery' | 'asset_change' | 'asset_exposure';
  category: 'asset';
  summary?: {
    total: number;
    byType: {
      new: number;
      modified: number;
      deleted: number;
      exposed: number;
    };
  };
}

// type Notification = FindingNotification | AssetNotification;

interface NotificationsMenuProps {}

export const NotificationsMenu: React.FC<NotificationsMenuProps> = () => {

  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const client = new NotificationsApi(getApiConfiguration(token));
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Dummy notifications data
  // const notifications: Notification[] = [
  //   {
  //     id: '1',
  //     category: 'finding',
  //     type: 'security',
  //     title: 'Multiple Security Issues Detected',
  //     description: 'New security findings have been detected',
  //     timestamp: '2024-02-19T17:23:29',
  //     read: false,
  //     summary: {
  //       total: 3,
  //       bySeverity: {
  //         critical: 1,
  //         high: 1,
  //         medium: 1,
  //         low: 0
  //       }
  //     }
  //   },
  //   {
  //     id: '2',
  //     category: 'asset',
  //     type: 'asset_discovery',
  //     title: 'New Assets Discovered',
  //     description: 'Multiple new assets found in your infrastructure',
  //     timestamp: '2024-02-19T16:45:00',
  //     read: true,
  //     summary: {
  //       total: 3,
  //       byType: {
  //         new: 2,
  //         modified: 1,
  //         deleted: 0,
  //         exposed: 0
  //       }
  //     }
  //   }
  // ];

  const onMarkAsRead = (id: string) => {
    console.log('Marking notification as read:', id);
  };
  /**
   * Returns the appropriate icon based on notification type
   */
  const getNotificationIcon = (notification: Notification) => {
    switch (notification.objectType) {
      case 'Domain': return Globe;
      case 'Subdomain': return Globe;
      case 'Ip_Address': return Globe;
      case 'Ports': return Globe;
      case "Finding": return Shield;
      case "Publicleak": return Key;
      case "Stealerleak": return Key;
      case "Dataleak": return HashIcon;
      default: return Bell;
    }

    // // Finding category
    // switch (notification.type) {
    //   case 'security': return Shield;
    //   case 'credential': return Key;
    //   case 'data': return FileWarning;
    //   case 'system': return Settings;
    //   default: return Bell;
    // }
  };

  // Icons will be according to objectType:
  // Assets: Domain, Subdomain, Service, Ip, Port, Cloud Resource will be Internet Icon
  // Findings will be Shield Icon
  // Credentials (Public, Stealer leaks) will be Key Icon
  // Data leaks will be Code icon or # Icon
  // All other types will be default Bell Icon

  // For Severity get the colors from security findings page and when severity is info or null show blue color
  // redesign the notification menu 
  // show notification time and severity at the end of the notification
  // add a mark as read button
  // add a mark all as read button
  // add a filter by type button
  // add a filter by severity button

  /**
   * Returns the appropriate color class based on notification type
   */
  const getNotificationColor = (notification: Notification) => {
    switch (notification.severity) {
      case 'critical': return 'text-red-500 bg-red-500/10';
      case 'high': return 'text-orange-500 bg-orange-500/10';
      case 'medium': return 'text-yellow-500 bg-yellow-500/10';
      case 'low': return 'text-emerald-500 bg-emerald-500/10';
      case "Finding": return 'text-red-500 bg-red-500/10';
      case "Publicleak": return 'text-blue-500 bg-blue-500/10';
      case "Stealerleak": return 'text-blue-500 bg-blue-500/10';
      case "Dataleak": return 'text-blue-500 bg-blue-500/10';
      // case 'high': return 'text-red-500 bg-red-500/10';
      // case 'info': return 'text-blue-500 bg-blue-500/10';
      // case 'medium': return 'text-purple-500 bg-purple-500/10';
      // case 'low': return 'text-emerald-500 bg-emerald-500/10';
      default: return 'text-blue-500 bg-blue-500/10';
    }
    
    // Asset category
    // switch (notification.eventType?.toLowerCase()) {
    //   case 'updated': return 'text-blue-500 bg-blue-500/10';
    //   case 'discovered': return 'text-yellow-500 bg-yellow-500/10';
    //   case 'exposure': return 'text-red-500 bg-red-500/10';
    //   default: return 'text-gray-500 bg-gray-500/10';
    // }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const fetchNotifications = useCallback(async () => {
    try {
      if (!currentTeam?.slug) return;
      const response = await client.notificationsNotificationsList({ teamSlug: currentTeam?.slug });
      console.log(response)
      setNotifications(response.results);
    } catch (error) {
      console.log(error);
    }
  }, [currentTeam?.slug, token]);

  const handleReadNotification = (notificationId: number, notification: Notification) => {
    if (!token || !currentTeam?.slug || !notificationId) return;

    client.notificationsNotificationsUpdate({ teamSlug: currentTeam?.slug, id: notificationId.toString(), notification: { ...notification, read: true } });
    setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
  };

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="p-2 rounded-lg hover:bg-[#1A1F2E] relative group">
          <Bell className="h-5 w-5 text-gray-400 group-hover:text-gray-300" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full" />
          )}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="w-96 bg-[#1A1F2E] rounded-lg border border-gray-800 shadow-xl animate-in fade-in-0 zoom-in-95"
          sideOffset={5}
          align="end"
        >
          <div className="px-3 py-2 border-b border-gray-800 flex items-center justify-between">
            <div className="font-medium text-sm text-white">Notifications</div>
            {unreadCount > 0 && (
              <div className="text-xs text-gray-500">{unreadCount} unread</div>
            )}
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {notifications.length > 0 ? (
              <div className="px-1.5 py-2">
                {notifications.map((notification) => {
                  const Icon = getNotificationIcon(notification);
                  const colorClass = getNotificationColor(notification);

                  return (
                    <div key={notification.id} className="px-2 py-2">
                      <DropdownMenu.Item asChild>
                        <div className="flex items-center space-x-3 rounded-md hover:bg-gray-800/50 cursor-pointer outline-none">
                          <Link 
                            to={`/notifications/${notification.objectType}/${notification.id}`}
                            onClick={() => handleReadNotification(notification.id, notification)}
                            className="flex-1 flex items-center justify-center space-x-3 min-h-6"
                          >
                            <div className={`p-2 rounded-lg ${colorClass} mt-0.5 relative`}>
                              <Icon className="h-4 w-4" />
                              {!notification.read && (
                                <span className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-300">
                                {notification.message}
                              </div>
                              {/* <div className="text-xs text-gray-500 mt-0.5">
                                {notification.description}
                              </div> */}
                            </div>
                            <div className="flex flex-col items-center space-x-2 mt-1.5">
                              <div className="flex items-center space-x-2 mt-1.5">
                                <span className="text-xs text-gray-500">
                                  {formatTimestamp(notification?.timestamp || '')}
                                </span>
                                <Clock className="h-3 w-3 text-gray-500" />

                              </div>
                              <div className="flex items-center space-x-2 mt-1.5">
                                {severityOptions.map((option) => {
                                  if (option.value === notification.severity) {
                                    return (
                                      <div
                                        key={option.value}
                                        className={cn(
                                          "flex items-center justify-between px-3 text-sm rounded-md cursor-pointer outline-none",
                                          option.color || "text-gray-300 hover:bg-gray-800/50",
                                          "data-[highlighted]:bg-gray-800/50"
                                        )}
                                      >
                                        {option.label}
                                      </div>
                                    );
                                  }
                                  return null;
                                })}
                              </div>
                            </div>
                          </Link>
                        </div>
                      </DropdownMenu.Item>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="px-3 py-8 text-center">
                <Bell className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                <div className="text-sm text-gray-500">No new notifications</div>
              </div>
            )}
          </div>

          <div className="px-1.5 py-2 border-t border-gray-800">
            <DropdownMenu.Item asChild>
              <Link
                to="/notifications"
                className="flex items-center justify-center px-2 py-2 rounded-md text-sm text-blue-500 hover:bg-gray-800/50 cursor-pointer outline-none"
              >
                <span>View all notifications</span>
                <ExternalLink className="h-4 w-4 ml-2" />
              </Link>
            </DropdownMenu.Item>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};